/**
 * W5 governance/projects/people-admin as fixture data — mirrors `fakeAutomation.ts`'s shape
 * (its own header explains why): the app must be demonstrable and testable with no backend,
 * and every gate this wave adds has to be *provable* offline, so this enforces the same
 * abilities it advertises rather than merely returning rows.
 *
 * Real per-user authorization only exists for a handful of these endpoints (§E-1/§E-8 of the
 * wave spec); everywhere else the platform checks nothing beyond "authenticated, same team".
 * Slate's decision is to gate those holes client-side on the *workspace* ability that already
 * gates the surface they live in (never on a row's own `abilities`, which `BuildPolicies`
 * stubs all-true) — and this fixture enforces exactly that, so a gating regression here means
 * a gating regression in the real UI too.
 */
import { ForbiddenError, NotFoundError } from "~/api/errors";
import {
  AUTH_ROLE_PERMISSIONS,
  type AuthIdentity,
  type AuthPolicy,
  type AuthRole,
  type AuthTeam,
  type AuthTeamMember,
  type AuthTeamMembership,
  type EntityAbilities,
  type GrantLevel,
  type GroupMemberRole,
  type GroupMembership,
  type InviteResult,
  type MemberInvite,
  type MemberRole,
  type Organization,
  type Project,
  type ProjectDraft,
  type ProjectGroupMembership,
  type ProjectMembership,
  type ProjectPatch,
  type ProjectStatus,
  type SourceApproval,
  type SourceApprovalStatus,
  type SourcePolicy,
  type Webhook,
  type WebhookDelivery,
  type WebhookDraft,
} from "~/api/governanceTypes";
import { ABILITY, type Abilities, type Collection, type Group, type Member } from "~/api/types";

const BASE_TIME = Date.parse("2026-08-12T09:00:00.000Z");
const hoursAgo = (h: number) => new Date(BASE_TIME - h * 3600_000).toISOString();

// ── Seeds ──────────────────────────────────────────────────────────────────

interface ProjectSeed extends Project {
  abilities: Record<string, boolean>;
}

const PROJECT_SEEDS: ProjectSeed[] = [
  {
    id: "proj-atlas",
    teamId: "ws-acme",
    groupId: "grp-eng",
    name: "Atlas",
    description: "The platform migration programme.",
    status: "active",
    icon: "🗺️",
    color: "#4C6EF5",
    startDate: hoursAgo(2000).slice(0, 10),
    endDate: null,
    ownerId: "user-1",
    createdById: "user-1",
    archivedAt: null,
    createdAt: hoursAgo(2000),
    updatedAt: hoursAgo(5),
    etag: 'W/"1"',
    abilities: { read: true, update: true, delete: true, share: false, managePermissions: true },
  },
  {
    id: "proj-hebrew",
    teamId: "ws-acme",
    groupId: null,
    name: "Hebrew Launch",
    description: "Right-to-left support across the app.",
    status: "onHold",
    icon: "🕊️",
    color: "#12B886",
    startDate: null,
    endDate: null,
    ownerId: "user-2",
    createdById: "user-2",
    archivedAt: null,
    createdAt: hoursAgo(900),
    updatedAt: hoursAgo(80),
    etag: 'W/"1"',
    // No managePermissions — proves the people dialog withholds itself for this project.
    abilities: { read: true, update: true, delete: true, share: false, managePermissions: false },
  },
  {
    id: "proj-sunset",
    teamId: "ws-acme",
    groupId: null,
    name: "Sunset — legacy importer",
    description: "Wound down; kept for the record.",
    status: "archived",
    icon: "🌇",
    color: "#868E96",
    startDate: null,
    endDate: hoursAgo(600).slice(0, 10),
    ownerId: "user-1",
    createdById: "user-1",
    archivedAt: hoursAgo(600),
    createdAt: hoursAgo(3000),
    updatedAt: hoursAgo(600),
    etag: 'W/"1"',
    // Archived ⇒ Edit is refused server-side (ProjectAuthorizationHandler) even though the
    // ability map otherwise looks editable — proven by forcing `update:false` below too.
    abilities: { read: true, update: false, delete: true, share: false, managePermissions: true },
  },
];

const PROJECT_MEMBERSHIP_SEEDS: Record<string, ProjectMembership[]> = {
  "proj-atlas": [
    { userId: "user-2", permission: "read_write" },
    { userId: "user-3", permission: "read" },
  ],
};

const PROJECT_GROUP_SEEDS: Record<string, ProjectGroupMembership[]> = {};

const GROUP_MEMBER_SEEDS: GroupMembership[] = [
  { id: "user-1-grp-eng", userId: "user-1", groupId: "grp-eng", role: "lead", user: null },
  { id: "user-2-grp-eng", userId: "user-2", groupId: "grp-eng", role: "member", user: null },
  { id: "user-3-grp-eng", userId: "user-3", groupId: "grp-eng", role: "member", user: null },
  { id: "user-1-grp-leads", userId: "user-1", groupId: "grp-leads", role: "lead", user: null },
  { id: "user-2-grp-leads", userId: "user-2", groupId: "grp-leads", role: "member", user: null },
  {
    id: "user-1-grp-everyone",
    userId: "user-1",
    groupId: "grp-everyone",
    role: "member",
    user: null,
  },
  {
    id: "user-2-grp-everyone",
    userId: "user-2",
    groupId: "grp-everyone",
    role: "member",
    user: null,
  },
  {
    id: "user-3-grp-everyone",
    userId: "user-3",
    groupId: "grp-everyone",
    role: "member",
    user: null,
  },
  {
    id: "user-4-grp-everyone",
    userId: "user-4",
    groupId: "grp-everyone",
    role: "member",
    user: null,
  },
  {
    id: "user-5-grp-everyone",
    userId: "user-5",
    groupId: "grp-everyone",
    role: "member",
    user: null,
  },
];

const AUTH_IDENTITY_SEED: AuthIdentity = {
  userId: "auth-1",
  email: "ada@example.com",
  name: "Ada Lovelace",
  avatarUrl: null,
  teams: [{ teamId: "team-1", role: "admin", status: "active" }],
  attributes: { department: "engineering", clearanceLevel: 3 },
};

const AUTH_TEAM_SEEDS: AuthTeam[] = [
  {
    id: "team-1",
    name: "Core",
    slug: "core",
    organizationId: "ws-acme",
    createdAt: hoursAgo(3000),
  },
  // A different org — proves the cross-tenant 404 (§A.1.2 A6).
  {
    id: "team-2",
    name: "Skunkworks",
    slug: "skunkworks",
    organizationId: "org-other",
    createdAt: hoursAgo(1000),
  },
];

const AUTH_TEAM_MEMBER_SEEDS: Record<string, AuthTeamMember[]> = {
  "team-1": [
    {
      userId: "user-1",
      teamId: "team-1",
      status: "active",
      role: "admin",
      name: "Ada Lovelace",
      email: "ada@example.com",
      teamName: "Core",
    },
    {
      userId: "user-2",
      teamId: "team-1",
      status: "active",
      role: "member",
      name: "Grace Hopper",
      email: "grace@example.com",
      teamName: "Core",
    },
  ],
};

const authRoleSeeds: AuthRole[] = [
  {
    id: "role-admin",
    name: "Admin",
    slug: "admin",
    description: "Full workspace control. System-defined.",
    isSystem: true,
    permissions: ["admin.*"],
    createdAt: hoursAgo(3000),
    updatedAt: hoursAgo(3000),
  },
  {
    id: "role-editor",
    name: "Editor",
    slug: "editor",
    description: "Can read and write documents and collections.",
    isSystem: false,
    permissions: ["documents.read", "documents.write", "collections.read", "collections.write"],
    createdAt: hoursAgo(1500),
    updatedAt: hoursAgo(200),
  },
  {
    id: "role-viewer",
    name: "Viewer",
    slug: "viewer",
    description: "Read-only.",
    isSystem: false,
    permissions: ["documents.read", "collections.read"],
    createdAt: hoursAgo(1500),
    updatedAt: hoursAgo(1500),
  },
];

const authPolicySeeds: AuthPolicy[] = [
  {
    id: "policy-permit-docs",
    name: "Permit document read/write",
    description: "The baseline grant for editors.",
    resourceType: "document",
    effect: "permit",
    priority: 100,
    actions: ["read", "write"],
    conditions: {},
    isActive: true,
    createdAt: hoursAgo(1000),
    updatedAt: hoursAgo(1000),
  },
  {
    id: "policy-deny-all",
    name: "Deny everything (disabled)",
    description: "Kept for reference; not enforced.",
    resourceType: "*",
    effect: "deny",
    priority: 900,
    actions: ["*"],
    conditions: { "resource.sensitivity": { eq: 1 } },
    isActive: false,
    createdAt: hoursAgo(2000),
    updatedAt: hoursAgo(2000),
  },
];

const organizationSeeds: Organization[] = [
  {
    id: "org-acme",
    name: "Acme",
    domain: "acme.example.com",
    settingsJson: null,
    createdAt: hoursAgo(4000),
    updatedAt: hoursAgo(10),
    planTier: "business",
    planClaimVersion: 3,
  },
  {
    id: "org-other",
    name: "Skunkworks Ltd",
    domain: null,
    settingsJson: null,
    createdAt: hoursAgo(1000),
    updatedAt: hoursAgo(1000),
    planTier: "free",
    planClaimVersion: 1,
  },
];

const sourcePolicySeed: SourcePolicy = {
  engineDefault: "duckduckgo",
  engineLocked: false,
  domainMode: "allow",
  domains: ["*.gov", "wikipedia.org", "docs.example.com"],
  requireSourceApproval: true,
  maxSourcesPerRun: 20,
  maxCrawlDepth: 2,
};

const sourceApprovalSeeds: SourceApproval[] = [
  {
    id: "src-approval-1",
    domain: "example.org",
    sampleUrl: "https://example.org/report",
    thinkerId: "thinker-1",
    documentId: "doc-1",
    status: "pending",
    createdAt: hoursAgo(6),
  },
  {
    id: "src-approval-2",
    domain: "news.example.net",
    sampleUrl: "https://news.example.net/story",
    thinkerId: "thinker-1",
    documentId: null,
    status: "pending",
    createdAt: hoursAgo(20),
  },
  {
    id: "src-approval-3",
    domain: "wikipedia.org",
    sampleUrl: "https://wikipedia.org/wiki/Example",
    thinkerId: null,
    documentId: null,
    status: "approved",
    createdAt: hoursAgo(200),
  },
];

const webhookSeeds: Webhook[] = [
  {
    id: "wh-1",
    teamId: "ws-acme",
    createdById: "user-1",
    url: "https://hooks.example.com/slate",
    secret: "••••ab12",
    events: ["runs.*", "documents.publish"],
    enabled: true,
    createdAt: hoursAgo(500),
    updatedAt: hoursAgo(30),
    lastDeliveryAt: hoursAgo(2),
  },
  {
    id: "wh-2",
    teamId: "ws-acme",
    createdById: "user-2",
    url: "https://hooks.example.com/disabled",
    secret: "••••9f00",
    events: ["security.authz_denied"],
    enabled: false,
    createdAt: hoursAgo(700),
    updatedAt: hoursAgo(700),
    lastDeliveryAt: null,
  },
];

const webhookDeliverySeeds: Record<string, WebhookDelivery[]> = {
  "wh-1": [
    {
      id: "del-1",
      subscriptionId: "wh-1",
      eventType: "documents.publish",
      status: "success",
      attempt: 1,
      statusCode: 200,
      error: null,
      createdAt: hoursAgo(2),
      updatedAt: hoursAgo(2),
      nextAttemptAt: null,
      completedAt: hoursAgo(2),
    },
    {
      id: "del-2",
      subscriptionId: "wh-1",
      eventType: "runs.completed",
      status: "pending",
      attempt: 1,
      statusCode: null,
      error: null,
      createdAt: hoursAgo(1),
      updatedAt: hoursAgo(1),
      nextAttemptAt: hoursAgo(0),
      completedAt: null,
    },
    {
      id: "del-3",
      subscriptionId: "wh-1",
      eventType: "runs.failed",
      status: "failed",
      attempt: 3,
      statusCode: 500,
      error: "connect ECONNREFUSED 203.0.113.5:443",
      createdAt: hoursAgo(10),
      updatedAt: hoursAgo(9),
      nextAttemptAt: null,
      completedAt: hoursAgo(9),
    },
  ],
};

let nextId = 100;
const freshId = (prefix: string) => `${prefix}-${nextId++}`;

/** The array at `record[key]`, creating it in place on first use. */
function ensureArray<T>(record: Record<string, T[]>, key: string): T[] {
  const existing = record[key];
  if (existing) {
    return existing;
  }
  const created: T[] = [];
  record[key] = created;
  return created;
}

export interface FakeGovernanceDeps {
  requireSession(): unknown;
  /** Workspace-level abilities — the only trustworthy gate for the E-1 holes (§E-8). */
  abilities(): Abilities;
  delay<T>(value: T): Promise<T>;
  viewerId(): string;
  members(): Member[];
  setMembers(next: Member[]): void;
  groups(): Group[];
  setGroups(next: Group[]): void;
  collections(): Collection[];
  setCollections(next: Collection[]): void;
}

export function createFakeGovernance(deps: FakeGovernanceDeps) {
  // Deep-cloned per instance, exactly like FLOW_SEEDS in fakeAutomation.ts — a shallow spread
  // would let one test's `webhooks.rotateSecret` leak into the next test's fresh FakeApi.
  let projects: ProjectSeed[] = PROJECT_SEEDS.map((p) => ({ ...p, abilities: { ...p.abilities } }));
  const projectMemberships: Record<string, ProjectMembership[]> = Object.fromEntries(
    Object.entries(PROJECT_MEMBERSHIP_SEEDS).map(([id, rows]) => [id, rows.map((r) => ({ ...r }))]),
  );
  const projectGroups: Record<string, ProjectGroupMembership[]> = Object.fromEntries(
    Object.entries(PROJECT_GROUP_SEEDS).map(([id, rows]) => [id, rows.map((r) => ({ ...r }))]),
  );
  let groupMembers: GroupMembership[] = GROUP_MEMBER_SEEDS.map((g) => ({ ...g }));
  const authTeams: AuthTeam[] = AUTH_TEAM_SEEDS.map((t) => ({ ...t }));
  const authTeamMembers: Record<string, AuthTeamMember[]> = Object.fromEntries(
    Object.entries(AUTH_TEAM_MEMBER_SEEDS).map(([id, rows]) => [id, rows.map((r) => ({ ...r }))]),
  );
  let authRoles: AuthRole[] = authRoleSeeds.map((r) => ({ ...r, permissions: [...r.permissions] }));
  let authPolicies: AuthPolicy[] = authPolicySeeds.map((p) => ({
    ...p,
    actions: [...p.actions],
    conditions: { ...p.conditions },
  }));
  let organizations: Organization[] = organizationSeeds.map((o) => ({ ...o }));
  let sourcePolicy: SourcePolicy = { ...sourcePolicySeed, domains: [...sourcePolicySeed.domains] };
  const sourceApprovals: SourceApproval[] = sourceApprovalSeeds.map((a) => ({ ...a }));
  let webhooks: Webhook[] = webhookSeeds.map((w) => ({ ...w, events: [...w.events] }));
  const deliveries: Record<string, WebhookDelivery[]> = Object.fromEntries(
    Object.entries(webhookDeliverySeeds).map(([id, rows]) => [id, rows.map((r) => ({ ...r }))]),
  );

  const requireWorkspaceUpdate = () => {
    deps.requireSession();
    // §E-1: five-plus endpoints have no real server-side check beyond team scope. Slate's
    // decision is a cosmetic client (and fixture) gate on the workspace ability that already
    // gates the surface — never invented per-row truth `BuildPolicies` does not provide.
    if (deps.abilities()[ABILITY.workspaceUpdate] !== true) {
      throw new ForbiddenError("You cannot administer this workspace");
    }
  };
  const requireManageMembers = () => {
    deps.requireSession();
    if (deps.abilities()[ABILITY.workspaceManageMembers] !== true) {
      throw new ForbiddenError("You cannot manage members in this workspace");
    }
  };
  const requireManageIntegrations = () => {
    deps.requireSession();
    // The real gate is `IResourceAuthorizer.EnsureAsync(Webhook(teamId), Admin)` on EVERY
    // action, reads included — the strictest gate in the wave (§A.5). Mirrored exactly.
    if (deps.abilities()[ABILITY.workspaceManageIntegrations] !== true) {
      throw new ForbiddenError("You cannot manage integrations in this workspace");
    }
  };

  const findProject = (id: string): ProjectSeed => {
    const found = projects.find((p) => p.id === id);
    if (!found) {
      throw new NotFoundError("Project not found");
    }
    return found;
  };
  const findMember = (id: string): Member => {
    const found = deps.members().find((m) => m.id === id);
    if (!found) {
      throw new NotFoundError("Member not found");
    }
    return found;
  };
  const findGroup = (id: string): Group => {
    const found = deps.groups().find((g) => g.id === id);
    if (!found) {
      throw new NotFoundError("Group not found");
    }
    return found;
  };

  const projectsApi = {
    list: async (query?: {
      status?: ProjectStatus;
      groupId?: string;
      limit?: number;
    }): Promise<Project[]> => {
      deps.requireSession();
      let rows = projects;
      if (query?.status) {
        rows = rows.filter((p) => p.status === query.status);
      }
      if (query?.groupId) {
        rows = rows.filter((p) => p.groupId === query.groupId);
      }
      return deps.delay(rows.map((p) => ({ ...p })));
    },

    mine: async (): Promise<Project[]> => {
      deps.requireSession();
      const mine = new Set(
        groupMembers.filter((m) => m.userId === deps.viewerId()).map((m) => m.groupId),
      );
      return deps.delay(
        projects
          .filter((p) => p.ownerId === deps.viewerId() || (p.groupId && mine.has(p.groupId)))
          .map((p) => ({ ...p })),
      );
    },

    abilities: async (ids: string[]): Promise<EntityAbilities[]> => {
      deps.requireSession();
      return deps.delay(
        ids
          .map((id) => projects.find((p) => p.id === id))
          .filter((p): p is ProjectSeed => Boolean(p))
          .map((p) => ({
            id: p.id,
            // Archived ⇒ Edit refused, mirroring `ProjectAuthorizationHandler` regardless of
            // what the seed otherwise says (the same rule `update()` enforces below).
            abilities: {
              ...p.abilities,
              update: p.abilities.update === true && p.status !== "archived",
            },
          })),
      );
    },

    create: async (input: ProjectDraft): Promise<Project> => {
      requireWorkspaceUpdate();
      const now = new Date().toISOString();
      const project: ProjectSeed = {
        id: freshId("proj"),
        teamId: "ws-acme",
        groupId: input.groupId ?? null,
        name: input.name,
        description: input.description ?? null,
        status: "active",
        icon: input.icon ?? null,
        color: input.color ?? null,
        startDate: input.startDate ?? null,
        endDate: input.endDate ?? null,
        ownerId: deps.viewerId(),
        createdById: deps.viewerId(),
        archivedAt: null,
        createdAt: now,
        updatedAt: now,
        etag: 'W/"1"',
        abilities: {
          read: true,
          update: true,
          delete: true,
          share: false,
          managePermissions: true,
        },
      };
      projects = [...projects, project];
      return deps.delay({ ...project });
    },

    update: async (input: ProjectPatch & { id: string }): Promise<Project> => {
      deps.requireSession();
      const project = findProject(input.id);
      if (project.status === "archived") {
        throw new ForbiddenError("An archived project cannot be edited");
      }
      if (project.abilities.update !== true) {
        throw new ForbiddenError("You cannot edit this project");
      }
      Object.assign(project, {
        ...(input.name !== undefined ? { name: input.name } : {}),
        ...(input.description !== undefined ? { description: input.description } : {}),
        ...(input.groupId !== undefined ? { groupId: input.groupId } : {}),
        ...(input.icon !== undefined ? { icon: input.icon } : {}),
        ...(input.color !== undefined ? { color: input.color } : {}),
        ...(input.status !== undefined ? { status: input.status } : {}),
        ...(input.startDate !== undefined ? { startDate: input.startDate } : {}),
        ...(input.endDate !== undefined ? { endDate: input.endDate } : {}),
        updatedAt: new Date().toISOString(),
        etag: `W/"${Date.now()}"`,
      });
      return deps.delay({ ...project });
    },

    archive: async (id: string): Promise<void> => {
      requireWorkspaceUpdate();
      const project = findProject(id);
      project.status = "archived";
      project.archivedAt = new Date().toISOString();
      await deps.delay(undefined);
    },

    restore: async (id: string): Promise<void> => {
      requireWorkspaceUpdate();
      const project = findProject(id);
      project.status = "active";
      project.archivedAt = null;
      await deps.delay(undefined);
    },

    delete: async (id: string): Promise<void> => {
      deps.requireSession();
      const project = findProject(id);
      if (project.abilities.delete !== true) {
        throw new ForbiddenError("You cannot delete this project");
      }
      projects = projects.filter((p) => p.id !== id);
      await deps.delay(undefined);
    },

    people: {
      list: async (id: string): Promise<{ memberships: ProjectMembership[]; users: Member[] }> => {
        deps.requireSession();
        const rows = projectMemberships[id] ?? [];
        const users = deps.members().filter((m) => rows.some((r) => r.userId === m.id));
        return deps.delay({
          memberships: rows.map((r) => ({ ...r })),
          users: users.map((u) => ({ ...u })),
        });
      },
      add: async (input: { id: string; userId: string; permission: GrantLevel }): Promise<void> => {
        deps.requireSession();
        const project = findProject(input.id);
        if (project.abilities.managePermissions !== true) {
          throw new ForbiddenError("You cannot manage this project's people");
        }
        findMember(input.userId);
        const rows = ensureArray(projectMemberships, input.id);
        const existing = rows.find((r) => r.userId === input.userId);
        if (existing) {
          existing.permission = input.permission;
        } else {
          rows.push({ userId: input.userId, permission: input.permission });
        }
        await deps.delay(undefined);
      },
      remove: async (input: { id: string; userId: string }): Promise<void> => {
        deps.requireSession();
        const project = findProject(input.id);
        if (project.abilities.managePermissions !== true) {
          throw new ForbiddenError("You cannot manage this project's people");
        }
        projectMemberships[input.id] = (projectMemberships[input.id] ?? []).filter(
          (r) => r.userId !== input.userId,
        );
        await deps.delay(undefined);
      },
      groups: async (id: string): Promise<ProjectGroupMembership[]> => {
        deps.requireSession();
        return deps.delay((projectGroups[id] ?? []).map((r) => ({ ...r })));
      },
      addGroup: async (input: {
        id: string;
        groupId: string;
        permission: GrantLevel;
      }): Promise<void> => {
        deps.requireSession();
        const project = findProject(input.id);
        if (project.abilities.managePermissions !== true) {
          throw new ForbiddenError("You cannot manage this project's people");
        }
        const group = findGroup(input.groupId);
        const rows = ensureArray(projectGroups, input.id);
        if (!rows.some((r) => r.groupId === input.groupId)) {
          rows.push({
            id: freshId("pg"),
            groupId: input.groupId,
            permission: input.permission,
            group: { id: group.id, name: group.name },
          });
        }
        await deps.delay(undefined);
      },
      removeGroup: async (input: { id: string; groupId: string }): Promise<void> => {
        deps.requireSession();
        const project = findProject(input.id);
        if (project.abilities.managePermissions !== true) {
          throw new ForbiddenError("You cannot manage this project's people");
        }
        projectGroups[input.id] = (projectGroups[input.id] ?? []).filter(
          (r) => r.groupId !== input.groupId,
        );
        await deps.delay(undefined);
      },
    },

    linkCollection: async (input: {
      collectionId: string;
      projectId: string | null;
    }): Promise<Collection> => {
      deps.requireSession();
      const collections = deps.collections();
      const collection = collections.find((c) => c.id === input.collectionId);
      if (!collection) {
        throw new NotFoundError("Collection not found");
      }
      // §E-1 (parenthetical): the real gate for this hole is `collectionUpdate`, not the
      // workspace ability — the same check `CollectionMenu`'s "Move to project…" entry uses.
      if (collection.abilities[ABILITY.collectionUpdate] !== true) {
        throw new ForbiddenError("You cannot move this collection");
      }
      if (input.projectId && !projects.some((p) => p.id === input.projectId)) {
        throw new NotFoundError("Project not found");
      }
      const updated = { ...collection, projectId: input.projectId };
      deps.setCollections(collections.map((c) => (c.id === updated.id ? updated : c)));
      return deps.delay({ ...updated });
    },

    anchorGroup: async (input: { groupId: string; projectId: string | null }): Promise<Group> => {
      deps.requireSession();
      const group = findGroup(input.groupId);
      if (group.abilities[ABILITY.collectionUpdate] !== true && group.abilities.update !== true) {
        throw new ForbiddenError("You cannot anchor this group");
      }
      if (input.projectId) {
        findProject(input.projectId).groupId = input.groupId;
      } else {
        for (const project of projects) {
          if (project.groupId === input.groupId) {
            project.groupId = null;
          }
        }
      }
      return deps.delay({ ...group });
    },
  };

  const peopleApi = {
    invite: async (invites: MemberInvite[]): Promise<InviteResult> => {
      requireManageMembers();
      const existing = new Set(deps.members().map((m) => m.email.toLowerCase()));
      const sent: Array<{ email: string }> = [];
      const created: Member[] = [];
      for (const invite of invites) {
        const email = invite.email.trim().toLowerCase();
        if (!email?.includes("@") || existing.has(email)) {
          continue; // silently skipped — a duplicate or malformed address, exactly as the
          // server does (§A.6.1): `sent.length` can be smaller than what was submitted.
        }
        const member: Member = {
          id: freshId("user"),
          name: invite.name ?? email.split("@")[0] ?? email,
          email: invite.email.trim(),
          avatarUrl: null,
          role: invite.role ?? "member",
          suspended: false,
          lastActiveAt: null,
          abilities: { update: true, delete: true },
        };
        created.push(member);
        sent.push({ email: invite.email.trim() });
        existing.add(email);
      }
      deps.setMembers([...deps.members(), ...created]);
      return deps.delay({ sent, users: created.map((m) => ({ ...m })) });
    },

    resendInvite: async (id: string): Promise<void> => {
      requireManageMembers();
      findMember(id);
      await deps.delay(undefined);
    },

    setRole: async (input: { id: string; role: MemberRole }): Promise<Member> => {
      requireManageMembers();
      if (input.id === deps.viewerId()) {
        throw new ForbiddenError("Cannot change your own role.");
      }
      const members = deps.members();
      const member = members.find((m) => m.id === input.id);
      if (!member) {
        throw new NotFoundError("Member not found");
      }
      const updated = { ...member, role: input.role };
      deps.setMembers(members.map((m) => (m.id === updated.id ? updated : m)));
      return deps.delay({ ...updated });
    },

    suspend: async (id: string): Promise<Member> => {
      requireManageMembers();
      const members = deps.members();
      const member = members.find((m) => m.id === id);
      if (!member) {
        throw new NotFoundError("Member not found");
      }
      const updated = { ...member, suspended: true };
      deps.setMembers(members.map((m) => (m.id === id ? updated : m)));
      return deps.delay({ ...updated });
    },

    activate: async (id: string): Promise<Member> => {
      requireManageMembers();
      const members = deps.members();
      const member = members.find((m) => m.id === id);
      if (!member) {
        throw new NotFoundError("Member not found");
      }
      const updated = { ...member, suspended: false };
      deps.setMembers(members.map((m) => (m.id === id ? updated : m)));
      return deps.delay({ ...updated });
    },

    remove: async (id: string): Promise<void> => {
      requireManageMembers();
      findMember(id);
      deps.setMembers(deps.members().filter((m) => m.id !== id));
      groupMembers = groupMembers.filter((g) => g.userId !== id);
      await deps.delay(undefined);
    },

    groups: {
      create: async (input: { name: string; externalId?: string }): Promise<Group> => {
        requireManageMembers();
        const name = input.name.trim();
        if (!name) {
          throw new ForbiddenError("Name is required.");
        }
        const group: Group = {
          id: freshId("grp"),
          name,
          memberCount: 0,
          abilities: { update: true, delete: true },
        };
        deps.setGroups([...deps.groups(), group]);
        return deps.delay({ ...group });
      },

      update: async (input: { id: string; name?: string; externalId?: string }): Promise<Group> => {
        requireManageMembers();
        const groups = deps.groups();
        const group = groups.find((g) => g.id === input.id);
        if (!group) {
          throw new NotFoundError("Group not found");
        }
        const updated = { ...group, ...(input.name !== undefined ? { name: input.name } : {}) };
        deps.setGroups(groups.map((g) => (g.id === updated.id ? updated : g)));
        return deps.delay({ ...updated });
      },

      delete: async (id: string): Promise<void> => {
        requireManageMembers();
        findGroup(id);
        deps.setGroups(deps.groups().filter((g) => g.id !== id));
        groupMembers = groupMembers.filter((m) => m.groupId !== id);
        await deps.delay(undefined);
      },

      members: async (id: string): Promise<{ memberships: GroupMembership[]; users: Member[] }> => {
        requireManageMembers();
        const rows = groupMembers.filter((m) => m.groupId === id);
        const users = deps.members().filter((m) => rows.some((r) => r.userId === m.id));
        return deps.delay({
          memberships: rows.map((r) => ({ ...r })),
          users: users.map((u) => ({ ...u })),
        });
      },

      addMember: async (input: { id: string; userId: string }): Promise<void> => {
        requireManageMembers();
        const group = findGroup(input.id);
        findMember(input.userId);
        if (!groupMembers.some((m) => m.groupId === input.id && m.userId === input.userId)) {
          groupMembers = [
            ...groupMembers,
            {
              id: `${input.userId}-${input.id}`,
              userId: input.userId,
              groupId: input.id,
              role: "member",
              user: null,
            },
          ];
          deps.setGroups(
            deps
              .groups()
              .map((g) => (g.id === group.id ? { ...g, memberCount: g.memberCount + 1 } : g)),
          );
        }
        await deps.delay(undefined);
      },

      removeMember: async (input: { id: string; userId: string }): Promise<void> => {
        requireManageMembers();
        const group = findGroup(input.id);
        const had = groupMembers.some((m) => m.groupId === input.id && m.userId === input.userId);
        groupMembers = groupMembers.filter(
          (m) => !(m.groupId === input.id && m.userId === input.userId),
        );
        if (had) {
          deps.setGroups(
            deps
              .groups()
              .map((g) =>
                g.id === group.id ? { ...g, memberCount: Math.max(0, g.memberCount - 1) } : g,
              ),
          );
        }
        await deps.delay(undefined);
      },

      setMemberRole: async (input: {
        id: string;
        userId: string;
        role: GroupMemberRole;
      }): Promise<void> => {
        requireManageMembers();
        const row = groupMembers.find((m) => m.groupId === input.id && m.userId === input.userId);
        if (!row) {
          throw new NotFoundError("Membership not found");
        }
        row.role = input.role;
        await deps.delay(undefined);
      },
    },
  };

  const governanceApi = {
    auth: {
      sync: async (): Promise<AuthIdentity> => {
        requireWorkspaceUpdate();
        return deps.delay({
          ...AUTH_IDENTITY_SEED,
          teams: AUTH_IDENTITY_SEED.teams.map((t) => ({ ...t })),
        });
      },
      myTeams: async (): Promise<AuthTeamMembership[]> => {
        deps.requireSession();
        return deps.delay(
          AUTH_IDENTITY_SEED.teams.map((membership) => {
            const team = authTeams.find((t) => t.id === membership.teamId);
            return {
              teamId: membership.teamId,
              ...(team
                ? { name: team.name, slug: team.slug, organizationId: team.organizationId }
                : {}),
              role: membership.role,
              status: membership.status,
            };
          }),
        );
      },
      teams: {
        list: async (): Promise<AuthTeam[]> => {
          requireWorkspaceUpdate();
          return deps.delay(authTeams.map((t) => ({ ...t })));
        },
        members: async (teamId: string): Promise<AuthTeamMember[]> => {
          deps.requireSession();
          const team = authTeams.find((t) => t.id === teamId);
          // Cross-tenant probe answers 404, never 403 — a 403 would confirm existence (§B.3).
          if (team?.organizationId !== "ws-acme") {
            throw new NotFoundError("Team not found");
          }
          return deps.delay((authTeamMembers[teamId] ?? []).map((m) => ({ ...m })));
        },
        create: async (input: {
          name: string;
          slug: string;
          organizationId: string;
        }): Promise<AuthTeam> => {
          requireWorkspaceUpdate();
          const team: AuthTeam = {
            id: freshId("team"),
            createdAt: new Date().toISOString(),
            ...input,
          };
          authTeams.push(team);
          return deps.delay({ ...team });
        },
        update: async (input: {
          teamId: string;
          name: string;
          slug: string;
        }): Promise<AuthTeam> => {
          requireWorkspaceUpdate();
          const team = authTeams.find((t) => t.id === input.teamId);
          if (!team) {
            throw new NotFoundError("Team not found");
          }
          team.name = input.name;
          team.slug = input.slug;
          return deps.delay({ ...team });
        },
        remove: async (teamId: string): Promise<void> => {
          requireWorkspaceUpdate();
          const index = authTeams.findIndex((t) => t.id === teamId);
          if (index === -1) {
            throw new NotFoundError("Team not found");
          }
          authTeams.splice(index, 1);
          await deps.delay(undefined);
        },
      },
      roles: {
        list: async (): Promise<AuthRole[]> => {
          requireWorkspaceUpdate();
          return deps.delay(authRoles.map((r) => ({ ...r, permissions: [...r.permissions] })));
        },
        create: async (input: {
          name: string;
          slug: string;
          description?: string;
          permissions: string[];
        }): Promise<AuthRole> => {
          requireWorkspaceUpdate();
          const now = new Date().toISOString();
          const role: AuthRole = {
            id: freshId("role"),
            name: input.name,
            slug: input.slug,
            description: input.description ?? null,
            isSystem: false,
            permissions: [...input.permissions],
            createdAt: now,
            updatedAt: now,
          };
          authRoles = [...authRoles, role];
          return deps.delay({ ...role });
        },
        update: async (input: {
          roleId: string;
          name: string;
          slug: string;
          description?: string;
          permissions: string[];
        }): Promise<AuthRole> => {
          requireWorkspaceUpdate();
          const role = authRoles.find((r) => r.id === input.roleId);
          if (!role) {
            throw new NotFoundError("Role not found");
          }
          if (role.isSystem) {
            throw new ForbiddenError("System roles cannot be edited.");
          }
          role.name = input.name;
          role.slug = input.slug;
          role.description = input.description ?? null;
          role.permissions = [...input.permissions];
          role.updatedAt = new Date().toISOString();
          return deps.delay({ ...role });
        },
        remove: async (roleId: string): Promise<void> => {
          requireWorkspaceUpdate();
          const role = authRoles.find((r) => r.id === roleId);
          if (!role) {
            throw new NotFoundError("Role not found");
          }
          if (role.isSystem) {
            throw new ForbiddenError("System roles cannot be deleted.");
          }
          authRoles = authRoles.filter((r) => r.id !== roleId);
          await deps.delay(undefined);
        },
      },
      policies: {
        list: async (): Promise<AuthPolicy[]> => {
          requireWorkspaceUpdate();
          return deps.delay(
            [...authPolicies].sort((a, b) => b.priority - a.priority).map((p) => ({ ...p })),
          );
        },
        create: async (input: {
          name: string;
          description?: string;
          resourceType: string;
          effect: "permit" | "deny";
          priority: number;
          actions: string[];
          conditions: Record<string, unknown>;
          isActive: boolean;
        }): Promise<AuthPolicy> => {
          requireWorkspaceUpdate();
          if (!input.name.trim()) {
            throw new ForbiddenError("Name is required.");
          }
          const now = new Date().toISOString();
          const policy: AuthPolicy = {
            id: freshId("policy"),
            name: input.name,
            description: input.description ?? null,
            resourceType: input.resourceType,
            effect: input.effect,
            priority: input.priority,
            actions: input.actions,
            conditions: input.conditions,
            isActive: input.isActive,
            createdAt: now,
            updatedAt: now,
          };
          authPolicies = [...authPolicies, policy];
          return deps.delay({ ...policy });
        },
        update: async (input: {
          policyId: string;
          name: string;
          description?: string;
          resourceType: string;
          effect: "permit" | "deny";
          priority: number;
          actions: string[];
          conditions: Record<string, unknown>;
          isActive: boolean;
        }): Promise<AuthPolicy> => {
          requireWorkspaceUpdate();
          const policy = authPolicies.find((p) => p.id === input.policyId);
          if (!policy) {
            throw new NotFoundError("Policy not found");
          }
          policy.name = input.name;
          policy.description = input.description ?? null;
          policy.resourceType = input.resourceType;
          policy.effect = input.effect;
          policy.priority = input.priority;
          policy.actions = input.actions;
          policy.conditions = input.conditions;
          policy.isActive = input.isActive;
          policy.updatedAt = new Date().toISOString();
          return deps.delay({ ...policy });
        },
        remove: async (policyId: string): Promise<void> => {
          requireWorkspaceUpdate();
          authPolicies = authPolicies.filter((p) => p.id !== policyId);
          await deps.delay(undefined);
        },
      },
    },

    organizations: {
      list: async (): Promise<Organization[]> => {
        requireWorkspaceUpdate();
        return deps.delay(organizations.map((o) => ({ ...o })));
      },
      create: async (input: { name: string; domain?: string }): Promise<Organization> => {
        requireWorkspaceUpdate();
        const now = new Date().toISOString();
        const org: Organization = {
          id: freshId("org"),
          name: input.name,
          domain: input.domain ?? null,
          settingsJson: null,
          createdAt: now,
          updatedAt: now,
          planTier: "free",
          planClaimVersion: 1,
        };
        organizations = [...organizations, org];
        return deps.delay({ ...org });
      },
      update: async (input: {
        id: string;
        name: string;
        domain?: string;
      }): Promise<Organization> => {
        requireWorkspaceUpdate();
        const org = organizations.find((o) => o.id === input.id);
        if (!org) {
          throw new NotFoundError("Organization not found");
        }
        org.name = input.name;
        org.domain = input.domain ?? org.domain;
        org.updatedAt = new Date().toISOString();
        return deps.delay({ ...org });
      },
      remove: async (id: string): Promise<void> => {
        requireWorkspaceUpdate();
        organizations = organizations.filter((o) => o.id !== id);
        await deps.delay(undefined);
      },
    },

    sourcePolicy: {
      get: async (): Promise<SourcePolicy> => {
        requireWorkspaceUpdate();
        return deps.delay({ ...sourcePolicy, domains: [...sourcePolicy.domains] });
      },
      save: async (policy: SourcePolicy): Promise<SourcePolicy> => {
        requireWorkspaceUpdate();
        sourcePolicy = { ...policy, domains: [...policy.domains] };
        return deps.delay({ ...sourcePolicy, domains: [...sourcePolicy.domains] });
      },
      approvals: async (status?: SourceApprovalStatus): Promise<SourceApproval[]> => {
        requireWorkspaceUpdate();
        const rows = status ? sourceApprovals.filter((a) => a.status === status) : sourceApprovals;
        return deps.delay(rows.map((a) => ({ ...a })));
      },
      decide: async (input: {
        id: string;
        status: "approved" | "rejected";
      }): Promise<SourceApproval> => {
        requireWorkspaceUpdate();
        const approval = sourceApprovals.find((a) => a.id === input.id);
        if (!approval) {
          throw new NotFoundError("Approval not found");
        }
        approval.status = input.status;
        return deps.delay({ ...approval });
      },
    },

    webhooks: {
      list: async (): Promise<Webhook[]> => {
        requireManageIntegrations();
        return deps.delay(webhooks.map((w) => ({ ...w, events: [...w.events] })));
      },
      create: async (input: WebhookDraft): Promise<Webhook> => {
        requireManageIntegrations();
        const now = new Date().toISOString();
        const webhook: Webhook = {
          id: freshId("wh"),
          teamId: "ws-acme",
          createdById: deps.viewerId(),
          url: input.url,
          secret: `whsec_${freshId("live")}`,
          events: [...input.events],
          enabled: input.enabled ?? true,
          createdAt: now,
          updatedAt: now,
          lastDeliveryAt: null,
        };
        webhooks = [...webhooks, webhook];
        return deps.delay({ ...webhook });
      },
      update: async (input: WebhookDraft & { id: string }): Promise<Webhook> => {
        requireManageIntegrations();
        const webhook = webhooks.find((w) => w.id === input.id);
        if (!webhook) {
          throw new NotFoundError("Webhook not found");
        }
        webhook.url = input.url;
        webhook.events = [...input.events];
        webhook.enabled = input.enabled ?? webhook.enabled;
        webhook.updatedAt = new Date().toISOString();
        // Reads mask the secret; the caller already only has the masked form to echo back.
        return deps.delay({ ...webhook });
      },
      remove: async (id: string): Promise<void> => {
        requireManageIntegrations();
        webhooks = webhooks.filter((w) => w.id !== id);
        await deps.delay(undefined);
      },
      rotateSecret: async (id: string): Promise<Webhook> => {
        requireManageIntegrations();
        const webhook = webhooks.find((w) => w.id === id);
        if (!webhook) {
          throw new NotFoundError("Webhook not found");
        }
        webhook.secret = `whsec_${freshId("rot")}`;
        webhook.updatedAt = new Date().toISOString();
        return deps.delay({ ...webhook });
      },
      deliveries: async (id: string): Promise<WebhookDelivery[]> => {
        requireManageIntegrations();
        return deps.delay((deliveries[id] ?? []).map((d) => ({ ...d })));
      },
    },
  };

  return { projects: projectsApi, people: peopleApi, governance: governanceApi };
}

/** The client-side convenience permission list — exported so the UI's checklist stays in sync. */
export { AUTH_ROLE_PERMISSIONS };
